import { SafetyControlSurveyNoteView } from "src/api/generated/erp/db/types/tables/safetyControlSurveyNoteView.ts";
import { SafetyControlSurveyNoteApi } from "src/api/generated/erp/safetyControlSurvey/api/safetyControlSurveyNoteApi.ts";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { dateTimeColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { SafetyControlSurveyNoteForm } from "./SafetyControlSurveyNoteForm.tsx";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { SafetyControlSurveyState } from "src/api/generated/erp/db/types/enums/safetyControlSurveyState.ts";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SafetyControlSurveyNotesDataGridProps {
	safetyControlSurveyId: number;
	surveyState: SafetyControlSurveyState | null | undefined;
	refreshRef?: RefreshableElementRef;
}

export const SafetyControlSurveyNotesDataGrid = ({
	safetyControlSurveyId,
	surveyState,
	refreshRef,
}: SafetyControlSurveyNotesDataGridProps) => {
	const isEditable = surveyState != null && surveyState !== "CLOSED";

	return (
		<ClientSideDataGridModel
			fetchData={() => SafetyControlSurveyNoteApi.getNotes({ safetyControlSurveyId })}
			getRows={(data) => data}
			getRowId={(row) => row.safetyControlSurveyNoteId}
			gridId={"A7F3B2C1D8E94F05"}
			initialParams={{}}
			refreshRef={refreshRef}
			render={({ dataGridProps, onlySelectedRow }) => (
				<FormCrudDataGrid<SafetyControlSurveyNoteView>
					columns={[
						textColumn({ field: "categoryName", headerName: i18n.t("category"), width: 150 }),
						textColumn({ field: "note", headerName: i18n.t("note"), width: 300 }),
						textColumn({ field: "createdByUserName", headerName: i18n.t("created_by"), width: 120 }),
						dateTimeColumn({ field: "createdAt", headerName: i18n.t("created_at"), width: 150 }),
					]}
					form={
						isEditable ?
							{
								addRowEnabled: true,
								editEnabled: true,
								dialogSize: "md",
								dialogTitle: i18n.t("note"),
								agentContext: (row) => ({
									key: defineAgentUiContextKey("erp.safetyControl.surveyNote.safetyControlSurveyNotesDataGrid.formDialog"),
									data: {
										safetyControlSurveyId,
										safetyControlSurveyNoteId: row?.safetyControlSurveyNoteId ?? null,
									},
								}),
								component: ({ row, onCompleted, onFormEdited }) => (
									<SafetyControlSurveyNoteForm
										note={row}
										safetyControlSurveyId={safetyControlSurveyId}
										onCompleted={onCompleted}
										onFormEdited={onFormEdited}
									/>
								),
							}
						:	undefined
					}
					remove={
						isEditable ?
							{
								type: "enabled",
								action: async ({ items }) => {
									for (const item of items) {
										await SafetyControlSurveyNoteApi.delete_({
											safetyControlSurveyNoteId: item.safetyControlSurveyNoteId,
										});
									}
								},
							}
						:	undefined
					}
					actionBarComponents={
						<DocumentsOfObjectButton
							objectRef={nullableAavoObjectRef(
								"SAFETY_CONTROL_SURVEY_NOTE",
								onlySelectedRow?.safetyControlSurveyNoteId,
							)}
						/>
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.safetyControl.surveyNotes.grid"),
						data: { safetyControlSurveyId },
						rowFields: ["safetyControlSurveyNoteId", "safetyControlSurveyId", "categoryId", "categoryName"],
						selectedRowPathSegment: (row) => `${i18n.t("note")} ${row.safetyControlSurveyNoteId}`,
						selectedRowsPathSegment: (count) => i18n.t("rows_selected_count", { count }),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
